import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';

import { Workstep, WorkstepTimeAdjustment, WorkstepTimeSegment } from '../../../../_models/workstep';
import { WorkstepService } from '../../workstep.service';

@Component({
  selector: 'app-workstep',
  templateUrl: './workstep-list.component.html',
  styleUrls: ['./workstep-list.component.css']
})
export class WorkstepListComponent implements OnInit {
  searchForm: FormGroup;
  workstepForm: FormGroup;
  workstepList: Workstep[];
  selectedWorkstep: Workstep;
  loading: boolean;
  saving: boolean;
  deleteModal: boolean;
  feedbackMessage: string;
  errorMessage: string;
  timeSegments: WorkstepTimeSegment[];
  timeAdjustments: WorkstepTimeAdjustment[];
  readonly timeMetricOptions = [
    { value: 'AREA_M2', label: 'Metros cuadrados (m²)' }, { value: 'LINEAR_M', label: 'Metros lineales (m)' },
    { value: 'UNITS', label: 'Unidades' }, { value: 'KG', label: 'Peso (kg)' }
  ];
  readonly timePropertyOptions = [
    { value: 'AREA_M2', label: 'Superficie (m²)' }, { value: 'LINEAR_M', label: 'Metros lineales (m)' },
    { value: 'UNITS', label: 'Unidades' }, { value: 'KG', label: 'Peso (kg)' },
    { value: 'MIN_SIDE_MM', label: 'Lado menor (mm)' }, { value: 'MAX_SIDE_MM', label: 'Lado mayor (mm)' },
    { value: 'STRAIGHT_SIDES', label: 'Nº lados rectos' }, { value: 'CURVED_SIDES', label: 'Nº lados curvos' },
    { value: 'SHAPE_NUMBER', label: 'Nº forma' }
  ];

  constructor(
    public router: Router,
    private formBuilder: FormBuilder,
    private workstepService: WorkstepService
  ) {}

  ngOnInit(): void {
    this.workstepList = [];
    this.selectedWorkstep = null;
    this.loading = false;
    this.saving = false;
    this.deleteModal = false;
    this.feedbackMessage = '';
    this.errorMessage = '';
    this.timeSegments = [];
    this.timeAdjustments = [];
    this.searchForm = this.formBuilder.group({
      f_search: [''],
      producttype: [''],
      stationsno: ['']
    });
    this.workstepForm = this.formBuilder.group({
      id: [null],
      name: ['', [Validators.required, Validators.maxLength(255)]],
      description: [''],
      producttype: [null, Validators.required],
      flip: [false],
      abbreviation: ['', Validators.maxLength(4)],
      stationsno: [1, [Validators.required, Validators.min(1), Validators.max(4)]],
      station0: [''], station1: [''], station2: [''], station3: [''],
      hours_per_day: [8, [Validators.required, Validators.min(0)]],
      seconds_per_kg: [0, [Validators.required, Validators.min(0)]],
      time_engine_enabled: [false], time_metric: ['KG', Validators.required], time_scope: ['PIECE', Validators.required],
      time_setup_seconds: [0, [Validators.required, Validators.min(0)]],
      time_load_seconds: [0, [Validators.required, Validators.min(0)]],
      time_unload_seconds: [0, [Validators.required, Validators.min(0)]]
    });
    this.loadWorksteps();
  }

  loadWorksteps(): void {
    this.loading = true;
    this.errorMessage = '';
    this.workstepService.loadWorksteps(this.searchForm.value).subscribe(data => {
      this.workstepList = data || [];
      this.loading = false;
    }, error => {
      this.loading = false;
      this.errorMessage = this.errorText(error);
    });
  }

  clearFilters(): void {
    this.searchForm.reset({ f_search: '', producttype: '', stationsno: '' });
    this.loadWorksteps();
  }

  createNew(): void {
    this.selectedWorkstep = null;
    this.feedbackMessage = '';
    this.errorMessage = '';
    this.workstepForm.reset({
      id: null, name: '', description: '', producttype: null, flip: false,
      abbreviation: '', stationsno: 1, station0: '', station1: '',
      station2: '', station3: '', hours_per_day: 8, seconds_per_kg: 0,
      time_engine_enabled: false, time_metric: 'KG', time_scope: 'PIECE',
      time_setup_seconds: 0, time_load_seconds: 0, time_unload_seconds: 0
    });
    this.timeSegments = [];
    this.timeAdjustments = [];
  }

  editWorkstep(workstepIn: Workstep): void {
    this.selectedWorkstep = workstepIn;
    this.feedbackMessage = '';
    this.errorMessage = '';
    this.workstepForm.reset({
      id: workstepIn.id,
      name: workstepIn.name || '',
      description: workstepIn.description || '',
      producttype: workstepIn.producttype === 0 || workstepIn.producttype === 1
        ? Number(workstepIn.producttype) : null,
      flip: workstepIn.flip === true || (workstepIn.flip as any) === 1,
      abbreviation: workstepIn.abbreviation || '',
      stationsno: Number(workstepIn.stationsno || 1),
      station0: workstepIn.station0 || '', station1: workstepIn.station1 || '',
      station2: workstepIn.station2 || '', station3: workstepIn.station3 || '',
      hours_per_day: this.round(Number(workstepIn.minutesaday || 0) / 60, 2),
      seconds_per_kg: this.round(Number(workstepIn.minutesakilo || 0) * 60, 3),
      time_engine_enabled: workstepIn.time_engine_enabled === true || (workstepIn.time_engine_enabled as any) === 1,
      time_metric: workstepIn.time_metric || 'KG', time_scope: workstepIn.time_scope || 'PIECE',
      time_setup_seconds: Number(workstepIn.time_setup_seconds || 0),
      time_load_seconds: Number(workstepIn.time_load_seconds || 0),
      time_unload_seconds: Number(workstepIn.time_unload_seconds || 0)
    });
    this.timeSegments = (workstepIn.time_segments || []).map((item, index) => ({
      id: item.id, sequence: Number(item.sequence || ((index + 1) * 10)), from_value: Number(item.from_value || 0),
      to_value: item.to_value === null || item.to_value === undefined ? null : Number(item.to_value),
      seconds: Number(item.seconds || 0),
      rate_mode: item.rate_value === null || item.rate_value === undefined
        ? 'FIXED_PER_PIECE' : (item.rate_mode || 'SECONDS_PER_UNIT'),
      rate_value: item.rate_value === null || item.rate_value === undefined ? Number(item.seconds || 0) : Number(item.rate_value),
      active: item.active !== false && (item.active as any) !== 0, notes: item.notes || ''
    }));
    this.timeAdjustments = (workstepIn.time_adjustments || []).map((item, index) => ({
      id: item.id, sequence: Number(item.sequence || ((index + 1) * 10)), name: item.name || '',
      percent: Number(item.percent || 0), mode: item.mode || 'PERCENT',
      value: item.value === null || item.value === undefined ? Number(item.percent || 0) : Number(item.value),
      active: item.active !== false && (item.active as any) !== 0,
      notes: item.notes || '', conditions: (item.conditions || []).map((condition, conditionIndex) => ({
        id: condition.id, sequence: Number(condition.sequence || ((conditionIndex + 1) * 10)),
        property: condition.property || 'KG', operator: condition.operator || 'BETWEEN',
        from_value: Number(condition.from_value || 0),
        to_value: condition.to_value === null || condition.to_value === undefined ? null : Number(condition.to_value),
        active: condition.active !== false && (condition.active as any) !== 0
      }))
    }));
  }

  addTimeSegment(): void {
    const previous = this.timeSegments.length ? this.timeSegments[this.timeSegments.length - 1] : null;
    this.timeSegments.push({ sequence: (this.timeSegments.length + 1) * 10,
      from_value: previous && previous.to_value !== null ? Number(previous.to_value) : 0,
      to_value: null, seconds: 0, rate_mode: 'SECONDS_PER_UNIT', rate_value: 0, active: true, notes: '' });
  }
  removeTimeSegment(indexIn: number): void { this.timeSegments.splice(indexIn, 1); this.resequence(this.timeSegments); }
  addTimeAdjustment(): void {
    this.timeAdjustments.push({ sequence: (this.timeAdjustments.length + 1) * 10, name: '', percent: 0, mode: 'PERCENT', value: 0,
      active: true, notes: '', conditions: [{ sequence: 10, property: 'KG', operator: 'BETWEEN',
        from_value: 0, to_value: null, active: true }] });
  }
  removeTimeAdjustment(indexIn: number): void { this.timeAdjustments.splice(indexIn, 1); this.resequence(this.timeAdjustments); }
  addTimeCondition(adjustmentIn: WorkstepTimeAdjustment): void {
    adjustmentIn.conditions = adjustmentIn.conditions || [];
    adjustmentIn.conditions.push({ sequence: (adjustmentIn.conditions.length + 1) * 10, property: 'KG',
      operator: 'BETWEEN', from_value: 0, to_value: null, active: true });
  }
  removeTimeCondition(adjustmentIn: WorkstepTimeAdjustment, indexIn: number): void {
    adjustmentIn.conditions.splice(indexIn, 1); this.resequence(adjustmentIn.conditions);
  }
  metricLabel(valueIn: string): string {
    const option = this.timeMetricOptions.find(item => item.value === valueIn);
    return option ? option.label : valueIn || 'Sin configurar';
  }
  metricRateLabel(valueIn: string, rateModeIn?: string): string {
    const metric = (valueIn || '').toString().toUpperCase();
    if ((rateModeIn || '').toString().toUpperCase() === 'FIXED_PER_PIECE') {
      return this.workstepForm.value.time_scope === 'POSITION' ? 'seg/posición' : 'seg/pieza';
    }
    if ((rateModeIn || '').toString().toUpperCase() === 'UNITS_PER_MINUTE') {
      if (metric === 'AREA_M2') return 'm²/min';
      if (metric === 'LINEAR_M') return 'm/min';
      if (metric === 'UNITS') return 'ud/min';
      if (metric === 'KG') return 'kg/min';
      return 'unidades/min';
    }
    if (metric === 'AREA_M2') return 'seg/m²';
    if (metric === 'LINEAR_M') return 'seg/m';
    if (metric === 'UNITS') return 'seg/ud';
    if (metric === 'KG') return 'seg/kg';
    return 'seg/unidad';
  }
  adjustmentUnitLabel(modeIn: string): string {
    if (modeIn === 'SECONDS_PER_PIECE') return 's/pieza';
    if (modeIn === 'SECONDS_PER_POSITION') return 's/posición';
    return '%';
  }
  timeRuleSummary(workstepIn: Workstep): string {
    if (!workstepIn || !(workstepIn.time_engine_enabled === true || (workstepIn.time_engine_enabled as any) === 1)) return 'Modelo heredado';
    const segments = (workstepIn.time_segments || []).filter(item => item.active !== false && (item.active as any) !== 0).length;
    const adjustments = (workstepIn.time_adjustments || []).filter(item => item.active !== false && (item.active as any) !== 0).length;
    return this.metricLabel(workstepIn.time_metric) + ' · ' + segments + ' segmentos · ' + adjustments + ' ajustes';
  }

  saveWorkstep(): void {
    if (this.saving) return;
    const validation = this.validateForm();
    if (validation) {
      this.errorMessage = validation;
      return;
    }
    const payload = this.normalizePayload();
    this.saving = true;
    this.feedbackMessage = '';
    this.errorMessage = '';
    const request = payload.id
      ? this.workstepService.updateWorkstep(payload)
      : this.workstepService.createWorkstep(payload);
    request.subscribe(data => {
      this.saving = false;
      if (!data) {
        this.errorMessage = 'No se ha podido guardar el paso de trabajo.';
        return;
      }
      this.editWorkstep(data);
      this.feedbackMessage = 'Paso de trabajo guardado correctamente.';
      this.loadWorksteps();
    }, error => {
      this.saving = false;
      this.errorMessage = this.errorText(error);
    });
  }

  askDelete(workstepIn: Workstep): void {
    this.selectedWorkstep = workstepIn;
    this.deleteModal = true;
  }

  confirmDelete(): void {
    if (!this.selectedWorkstep) return;
    this.workstepService.deleteWorkstep(this.selectedWorkstep).subscribe(result => {
      this.deleteModal = false;
      if (result === null || result === undefined) {
        this.errorMessage = 'No se ha podido eliminar el paso de trabajo.';
        return;
      }
      this.createNew();
      this.feedbackMessage = 'Paso de trabajo eliminado correctamente.';
      this.loadWorksteps();
    }, error => {
      this.deleteModal = false;
      this.errorMessage = this.errorText(error);
    });
  }

  stationCount(): number {
    return Math.max(1, Math.min(4, Number(this.workstepForm.controls.stationsno.value || 1)));
  }

  productTypeLabel(valueIn: any): string {
    if (Number(valueIn) === 0) return 'Producto final';
    if (Number(valueIn) === 1) return 'Componente';
    return 'Sin configurar';
  }

  stationSummary(workstepIn: Workstep): string {
    const count = Math.max(1, Number(workstepIn && workstepIn.stationsno || 1));
    if (count === 1) return '1 estación';
    const names = [workstepIn.station0, workstepIn.station1, workstepIn.station2, workstepIn.station3]
      .slice(0, count).filter(item => !!item);
    return count + ' estaciones' + (names.length > 0 ? ' · ' + names.join(' / ') : '');
  }

  hoursPerDay(workstepIn: Workstep): string {
    return this.round(Number(workstepIn && workstepIn.minutesaday || 0) / 60, 2).toString();
  }

  secondsPerKg(workstepIn: Workstep): string {
    return this.round(Number(workstepIn && workstepIn.minutesakilo || 0) * 60, 3).toString();
  }

  private validateForm(): string {
    if (this.workstepForm.invalid) {
      return 'Revisa el nombre, el tipo de producto, la jornada, el ritmo y el número de estaciones.';
    }
    const count = this.stationCount();
    if (count > 1) {
      for (let index = 0; index < count; index += 1) {
        const value = this.workstepForm.controls['station' + index].value;
        if (!value || value.toString().trim() === '') return 'Indica el nombre de las ' + count + ' estaciones.';
      }
    }
    if (this.workstepForm.value.time_engine_enabled) {
      if (!this.timeSegments.some(item => item.active)) return 'Añade al menos un segmento activo para calcular el tiempo.';
      for (const segment of this.timeSegments) {
        if (segment.from_value === null || Number(segment.from_value) < 0 || Number(segment.rate_value) < 0 ||
            (segment.rate_mode === 'UNITS_PER_MINUTE' && Number(segment.rate_value) <= 0)) return 'Revisa los valores y el ritmo de la tabla de tiempos.';
        if (segment.to_value !== null && segment.to_value !== undefined && Number(segment.to_value) <= Number(segment.from_value)) return 'En cada segmento, Hasta debe ser mayor que Desde.';
      }
    }
    for (const adjustment of this.timeAdjustments) {
      if (!adjustment.name || !adjustment.name.trim()) return 'Indica el nombre de cada ajuste fino.';
      if (adjustment.mode === 'PERCENT' && Number(adjustment.value) < -100) return 'Un ajuste porcentual no puede ser inferior a -100%.';
      if (adjustment.mode !== 'PERCENT' && Number(adjustment.value) < 0) return 'Un ajuste de tiempo no puede ser negativo.';
      if (adjustment.active && !(adjustment.conditions || []).some(item => item.active)) return 'Cada ajuste fino activo necesita al menos una condición.';
      for (const condition of adjustment.conditions || []) {
        if (condition.from_value === null || condition.from_value === undefined) return 'Indica el valor de cada condición.';
        if (condition.operator === 'BETWEEN' && (condition.to_value === null || condition.to_value === undefined || Number(condition.to_value) < Number(condition.from_value))) return 'Completa correctamente los intervalos Desde-Hasta.';
      }
    }
    return '';
  }

  private normalizePayload(): Workstep {
    const raw = this.workstepForm.value;
    const count = this.stationCount();
    return {
      id: raw.id ? Number(raw.id) : null,
      name: (raw.name || '').trim(),
      description: (raw.description || '').trim(),
      producttype: Number(raw.producttype),
      flip: raw.flip === true,
      abbreviation: (raw.abbreviation || '').trim().toUpperCase(),
      stationsno: count,
      station0: count > 1 ? (raw.station0 || '').trim() : null,
      station1: count > 1 ? (raw.station1 || '').trim() : null,
      station2: count > 2 ? (raw.station2 || '').trim() : null,
      station3: count > 3 ? (raw.station3 || '').trim() : null,
      minutesaday: Number(raw.hours_per_day || 0) * 60,
      minutesakilo: Number(raw.seconds_per_kg || 0) / 60,
      time_engine_enabled: raw.time_engine_enabled === true, time_metric: raw.time_metric || 'KG', time_scope: raw.time_scope || 'PIECE',
      time_setup_seconds: Number(raw.time_setup_seconds || 0), time_load_seconds: Number(raw.time_load_seconds || 0),
      time_unload_seconds: Number(raw.time_unload_seconds || 0),
      time_segments: this.timeSegments.map((item, index) => ({ sequence: (index + 1) * 10,
        from_value: Number(item.from_value || 0),
        to_value: item.to_value === null || item.to_value === undefined || (item.to_value as any) === '' ? null : Number(item.to_value),
        seconds: item.rate_mode === 'UNITS_PER_MINUTE' ? (Number(item.rate_value) > 0 ? 60 / Number(item.rate_value) : 0) : Number(item.rate_value || 0),
        rate_mode: item.rate_mode || 'SECONDS_PER_UNIT', rate_value: Number(item.rate_value || 0),
        active: item.active !== false, notes: (item.notes || '').trim() })),
      time_adjustments: this.timeAdjustments.map((item, index) => ({ sequence: (index + 1) * 10,
        name: (item.name || '').trim(), percent: item.mode === 'PERCENT' ? Number(item.value || 0) : 0,
        mode: item.mode || 'PERCENT', value: Number(item.value || 0), active: item.active !== false, notes: (item.notes || '').trim(),
        conditions: (item.conditions || []).map((condition, conditionIndex) => ({ sequence: (conditionIndex + 1) * 10,
          property: condition.property, operator: condition.operator, from_value: Number(condition.from_value || 0),
          to_value: condition.to_value === null || condition.to_value === undefined || (condition.to_value as any) === '' ? null : Number(condition.to_value), active: condition.active !== false })) }))
    } as Workstep;
  }

  private resequence(itemsIn: Array<{ sequence: number }>): void {
    (itemsIn || []).forEach((item, index) => item.sequence = (index + 1) * 10);
  }

  private round(valueIn: number, decimalsIn: number): number {
    const factor = Math.pow(10, decimalsIn);
    return Math.round((Number(valueIn) || 0) * factor) / factor;
  }

  private errorText(errorIn: any): string {
    if (errorIn && errorIn.error && errorIn.error.msg) return errorIn.error.msg;
    return errorIn && errorIn.message ? errorIn.message : 'No se ha podido completar la operación.';
  }
}
